import {
    categoryLayoutVariantDefs,
    ONE_DRAW_DEFAULT_MAXFILL_CATEGORY,
} from '../../../../../services/layoutVariantPrompts';
import type { SavedRoofModulesLayout } from '../../../../../@types/types';
import { canonicalCategoryName } from './resolveRoofCombinationsForRunAi';
import { useViewerStore } from '../../../../../store/store';

export type CategoryLayoutCandidate = {
    key: string;
    source: 'variant' | 'layoutKey';
    variantId?: string;
    label?: string;
    /** Main-card title (e.g. "Aesthetic - Portrait - North"). */
    displayLabel?: string;
    roofIds: string[];
    /** Portrait / landscape used when this candidate was simulated. */
    dir?: 'port' | 'land' | 'both' | null;
    minEff?: number | null;
    production: number;
    modules: number;
    efficiency: number | null;
    solarAccess: number | null;
    multiplier: number | null;
    snapshotUrl?: string;
    snapshotError?: string;
    treeCount?: number | null;
    roofCount?: number | null;
    /** Exact module placement captured when this candidate was simulated. */
    modulesLayout?: SavedRoofModulesLayout;
};

/** Treat 0 / missing as “no efficiency threshold” (variant generator stores 0 for null). */
export function normalizeLayoutMinEff(value: unknown): number | null {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
    return value;
}

const CATEGORY_MAIN_KEY_PREFIX = '__category_main__:';

export function getCategoryMainLayoutKey(category: string): string {
    return `${CATEGORY_MAIN_KEY_PREFIX}${category}`;
}

/**
 * Physical layout identity — roofs + simulated metrics only (no fill dir / minEff).
 * Used for cross-category dedup so the same max-fill design is not shown on multiple cards
 * when one category won it as a variant and another would take the `default` layout key.
 */
export function physicalLayoutSignature(c: CategoryLayoutCandidate): string {
    return JSON.stringify({
        roofs: [...c.roofIds].sort(),
        prod: Math.round(c.production),
        modules: c.modules,
        eff: c.efficiency != null && Number.isFinite(c.efficiency) ? Math.round(c.efficiency) : null,
        solar: Math.round(c.solarAccess ?? 0),
        mult: c.multiplier != null && Number.isFinite(c.multiplier) ? c.multiplier : null,
    });
}

export function roofFaceIdSetsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const norm = (ids: string[]) => [...ids].filter(Boolean).sort().join('|');
    return norm(a) === norm(b);
}

/** Stable signature for comparing whether two candidates represent the same physical layout. */
export function candidateLayoutSignature(c: CategoryLayoutCandidate): string {
    return JSON.stringify({
        roofs: [...c.roofIds].sort(),
        prod: Math.round(c.production),
        modules: c.modules,
        dir: c.dir ?? null,
        minEff: c.minEff ?? null,
        eff: c.efficiency != null && Number.isFinite(c.efficiency) ? Math.round(c.efficiency) : null,
        solar: Math.round(c.solarAccess ?? 0),
        mult: c.multiplier != null && Number.isFinite(c.multiplier) ? c.multiplier : null,
    });
}

/**
 * Primary catalog-row candidate — must match `categoryMainLayoutByCategory` (what the card
 * metrics show), not raw `rankCategoryCandidates()[0]` which ignores cross-category dedup.
 */
export function resolveCategoryMainCardCandidate(
    category: string,
    ranked: CategoryLayoutCandidate[],
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>,
): CategoryLayoutCandidate | undefined {
    const stored = categoryMainLayoutByCategory?.[category];
    if (stored?.roofIds?.length) {
        const catKey = getCategoryMainLayoutKey(category);
        const storedWithKey: CategoryLayoutCandidate = {
            ...stored,
            key: catKey,
            roofIds: stored.roofIds.filter(Boolean),
        };
        const storedSig = candidateLayoutSignature(storedWithKey);
        const matchInRanked = ranked.find((c) => candidateLayoutSignature(c) === storedSig);
        return matchInRanked ?? storedWithKey;
    }
    return ranked[0];
}

export type CategoryApplyLayoutParams = {
    roofFaceIds: string[];
    dir: 'port' | 'land' | 'both' | null;
    minEff: number | null;
    modulesLayout?: SavedRoofModulesLayout;
};

/**
 * Roof set + fill options for applying the main card of a design category.
 *
 * Resolution priority for the exact placement (`modulesLayout`):
 *   1. The winner candidate stored in `categoryMainLayoutByCategory[category]`.
 *   2. The winning variant's captured layout (matched by `variantId`).
 *   3. The exact placement cached by layout key in `layoutModulesLayoutByKey`
 *      (keyed either by the category-main key or the winner's own combo key).
 *   4. The first dashboard catalog candidate for the category.
 *
 * Steps 3–4 are the safety net that prevents `handleApplyLayoutClick` /
 * `runUseThisLayoutAction` from silently falling back to a full default max-fill
 * (a completely different design) when the winner record lost its `modulesLayout`
 * after persistence / hydration.
 */
export function resolveCategoryApplyLayoutParams(
    categoryLabel: string | undefined,
    categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate> | undefined,
    layoutCandidatesByCategory: Record<
        string,
        Array<{
            key?: string;
            roofFaceIds?: string[];
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            modulesLayout?: SavedRoofModulesLayout;
        }>
    >,
    variantsByCategory?: Record<string, unknown[]>,
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>,
): CategoryApplyLayoutParams | null {
    if (!categoryLabel) return null;

    const main = categoryMainLayoutByCategory?.[categoryLabel];
    const variant =
        main?.variantId && variantsByCategory?.[categoryLabel]
            ? (variantsByCategory[categoryLabel] as Array<{ id?: string; modulesLayout?: SavedRoofModulesLayout }>).find(
                  (v) => v?.id === main.variantId,
              )
            : undefined;

    /** Look up a non-empty cached exact placement by any of the supplied keys. */
    const cachedLayoutByKey = (...keys: Array<string | undefined>): SavedRoofModulesLayout | undefined => {
        if (!layoutModulesLayoutByKey) return undefined;
        for (const k of keys) {
            if (!k) continue;
            const cached = layoutModulesLayoutByKey[k];
            if (Array.isArray(cached) && cached.length) return cached;
        }
        return undefined;
    };

    const roofCombinationsFromAi =
        ((useViewerStore.getState() as any).aiResolvedRoofCombinations as
            | Array<{ name: string; roofFaceIds?: string[] }>
            | undefined) ?? [];
    const comboLayoutKey = resolveLayoutKeyForCategory(categoryLabel, roofCombinationsFromAi);

    const modulesLayout =
        (main?.modulesLayout?.length ? main.modulesLayout : undefined) ??
        ((variant as { modulesLayout?: SavedRoofModulesLayout } | undefined)?.modulesLayout?.length
            ? (variant as { modulesLayout: SavedRoofModulesLayout }).modulesLayout
            : undefined) ??
        cachedLayoutByKey(
            getCategoryMainLayoutKey(categoryLabel),
            main?.key,
            categoryLabel,
            comboLayoutKey,
        );

    const roofIds = (
        (variant as { roofIds?: string[] } | undefined)?.roofIds ??
        main?.roofIds ??
        []
    ).filter(Boolean) as string[];

    if (roofIds.length || modulesLayout?.length) {
        return {
            roofFaceIds: roofIds,
            dir:
                (variant as { dir?: 'port' | 'land' | 'both' | null } | undefined)?.dir ??
                main?.dir ??
                null,
            minEff: normalizeLayoutMinEff(
                (variant as { minEff?: number | null } | undefined)?.minEff ?? main?.minEff,
            ),
            modulesLayout,
        };
    }

    const fallback = layoutCandidatesByCategory[categoryLabel]?.[0];
    if (fallback?.roofFaceIds?.length || fallback?.modulesLayout?.length) {
        return {
            roofFaceIds: (fallback.roofFaceIds ?? []).filter(Boolean),
            dir: fallback.dir ?? null,
            minEff: normalizeLayoutMinEff(fallback.minEff),
            modulesLayout:
                (fallback.modulesLayout?.length ? fallback.modulesLayout : undefined) ??
                cachedLayoutByKey(fallback.key),
        };
    }

    return null;
}

/** Map a design category to the roof-combo layout key used for its base roof set. */
export function resolveLayoutKeyForCategory(
    category: string,
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>,
): string {
    const target = canonicalCategoryName(category).toLowerCase();
    if (target.includes('onedraw') && target.includes('maxfill')) return 'default';

    const exact = roofCombinationsFromAi.find(
        (c) => canonicalCategoryName(c.name).toLowerCase() === target,
    );
    if (exact?.name) return exact.name;

    if (target.includes('max production') || target.includes('production')) {
        const mp = roofCombinationsFromAi.find((c) =>
            /max(imum)?\s+production/i.test(c.name),
        );
        if (mp?.name) return mp.name;
    }
    if (target.includes('solar access')) {
        const sa = roofCombinationsFromAi.find((c) => /solar\s+access/i.test(c.name));
        if (sa?.name) return sa.name;
    }
    if (target.includes('efficiency')) {
        const eff = roofCombinationsFromAi.find((c) => /efficien/i.test(c.name));
        if (eff?.name) return eff.name;
    }
    if (target.includes('multiplier')) {
        const mult = roofCombinationsFromAi.find((c) => /multiplier/i.test(c.name));
        if (mult?.name) return mult.name;
    }
    if (target.includes('balanced')) {
        const bal = roofCombinationsFromAi.find((c) => /balanced/i.test(c.name));
        if (bal?.name) return bal.name;
    }
    if (target.includes('aesthetic')) {
        const aes = roofCombinationsFromAi.find((c) => /aesthetic/i.test(c.name));
        if (aes?.name) return aes.name;
    }
    return 'default';
}

/** Categories whose main card must come from per-category "More layouts" variants only. */
export function isVariantOnlyCategory(category: string): boolean {
    return category.trim().toLowerCase().includes('aesthetic');
}

/** Build main-card title, e.g. "Aesthetic - Portrait - North". */
export function formatCategoryWinnerDisplayLabel(
    category: string,
    candidate: Pick<CategoryLayoutCandidate, 'label' | 'dir'>,
): string {
    const rawLabel = (candidate.label ?? '').trim();
    if (!rawLabel) return category;

    const lower = rawLabel.toLowerCase();
    if (lower.includes('mixed')) {
        return `${category} - Mixed`;
    }

    const orientation = lower.includes('landscape')
        ? 'Landscape'
        : lower.includes('portrait')
          ? 'Portrait'
          : candidate.dir === 'land'
            ? 'Landscape'
            : candidate.dir === 'port'
              ? 'Portrait'
              : candidate.dir === 'both'
                ? 'Mixed'
                : null;

    const sides = ['north', 'south', 'east', 'west'] as const;
    const matchedSide = sides.find((side) => lower.includes(side));
    const sideTitle = matchedSide
        ? matchedSide.charAt(0).toUpperCase() + matchedSide.slice(1)
        : null;

    if (orientation && sideTitle) {
        return `${category} - ${orientation} - ${sideTitle}`;
    }
    if (orientation) return `${category} - ${orientation}`;
    if (sideTitle) return `${category} - ${sideTitle}`;

    return category;
}

/**
 * Label shown on the main category card in the AI designs catalog.
 * Only the category title is shown (orientation / side suffixes are intentionally omitted).
 */
export function resolveCategoryCardDisplayLabel(
    category: string,
    _categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>,
    _fallbackCandidate?: Pick<CategoryLayoutCandidate, 'label' | 'dir' | 'displayLabel'>,
): string {
    return category;
}

function scoreForCategory(category: string, c: CategoryLayoutCandidate): number {
    const normalized = category.trim().toLowerCase();
    if (normalized.includes('onedraw') && normalized.includes('maxfill')) {
        return c.production;
    }
    if (normalized.includes('production')) return c.production;
    if (normalized.includes('solar access')) return c.solarAccess ?? 0;
    if (normalized.includes('efficiency')) return c.efficiency ?? 0;
    if (normalized.includes('multiplier')) return c.multiplier ?? 0;
    if (normalized.includes('aesthetic')) return c.production;
    if (normalized.includes('balanced')) return c.production * 0.6 + (c.solarAccess ?? 0) * 100;
    return c.production;
}

function dedupeCandidates(candidates: CategoryLayoutCandidate[]): CategoryLayoutCandidate[] {
    const seen = new Set<string>();
    const deduped: CategoryLayoutCandidate[] = [];
    for (const c of candidates) {
        const sig = physicalLayoutSignature(c);
        if (seen.has(sig)) continue;
        seen.add(sig);
        deduped.push(c);
    }
    return deduped;
}

export function rankCategoryCandidates(
    category: string,
    candidates: CategoryLayoutCandidate[],
): CategoryLayoutCandidate[] {
    const viable = candidates.filter((c) => c.modules > 0 && c.roofIds.length > 0);
    return dedupeCandidates(viable).sort((a, b) => scoreForCategory(category, b) - scoreForCategory(category, a));
}

type CollectArgs = {
    category: string;
    variantsByCategory: Record<string, unknown[]>;
    layoutMetricsByKey: Record<string, { efficiency?: number | null; solarAccess?: number | null; treeCount?: number | null; roofCount?: number | null; multiplier?: number | null }>;
    layoutProductionByKey: Record<string, number>;
    layoutModulesByKey: Record<string, number>;
    layoutSnapshotsByKey: Record<string, string | undefined>;
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>;
    roofIdsByLayoutKey: (key: string) => string[];
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    roofCombinationsFromAi?: Array<{ name: string; roofFaceIds?: string[] }>;
    /**
     * When true, skip the "recycled" candidates that reuse the previous winner
     * (`categoryMainLayoutByCategory[category]`) or the `__category_main__:` map values.
     * Those join production/modules from one generation with roof IDs from another and can be
     * internally inconsistent. The run-time winner selection passes this so a fresh re-run
     * always picks a candidate whose production/modules/roofIds/modulesLayout were captured
     * together (variants + freshly simulated roof combos). Display/ranking keeps them (default false).
     */
    excludeRecycledMainCandidate?: boolean;
};

function pushLayoutKeyCandidate(
    candidates: CategoryLayoutCandidate[],
    layoutKey: string,
    args: CollectArgs,
): void {
    const {
        layoutMetricsByKey,
        layoutProductionByKey,
        layoutModulesByKey,
        layoutSnapshotsByKey,
        roofIdsByLayoutKey,
        layoutModulesLayoutByKey,
    } = args;

    if (layoutKey.startsWith(CATEGORY_MAIN_KEY_PREFIX)) return;
    if (candidates.some((c) => c.key === layoutKey)) return;

    const metrics = layoutMetricsByKey[layoutKey];
    if (!metrics) return;

    const roofIds = roofIdsByLayoutKey(layoutKey);
    if (!roofIds.length) return;

    const production =
        typeof layoutProductionByKey[layoutKey] === 'number' &&
        Number.isFinite(layoutProductionByKey[layoutKey])
            ? layoutProductionByKey[layoutKey]
            : 0;
    const modules =
        typeof layoutModulesByKey[layoutKey] === 'number' &&
        Number.isFinite(layoutModulesByKey[layoutKey])
            ? layoutModulesByKey[layoutKey]
            : 0;
    if (production <= 0 || modules <= 0) return;

    candidates.push({
        key: layoutKey,
        source: 'layoutKey',
        roofIds,
        production,
        modules,
        efficiency:
            typeof metrics?.efficiency === 'number' && Number.isFinite(metrics.efficiency)
                ? metrics.efficiency
                : null,
        solarAccess:
            typeof metrics?.solarAccess === 'number' && Number.isFinite(metrics.solarAccess)
                ? metrics.solarAccess
                : null,
        multiplier:
            typeof metrics?.multiplier === 'number' && Number.isFinite(metrics.multiplier)
                ? metrics.multiplier
                : null,
        snapshotUrl: layoutSnapshotsByKey[layoutKey],
        treeCount: metrics?.treeCount ?? null,
        roofCount:
            metrics?.roofCount != null && Number.isFinite(metrics.roofCount) && metrics.roofCount > 0
                ? metrics.roofCount
                : null,
        modulesLayout: layoutModulesLayoutByKey?.[layoutKey],
    });
}

export function collectCategoryCandidates(args: CollectArgs): CategoryLayoutCandidate[] {
    const {
        category,
        variantsByCategory,
        layoutMetricsByKey,
        layoutProductionByKey,
        layoutModulesByKey,
        layoutSnapshotsByKey,
        roofIdsByLayoutKey,
        categoryMainLayoutByCategory,
        roofCombinationsFromAi = [],
        excludeRecycledMainCandidate = false,
    } = args;

    const candidates: CategoryLayoutCandidate[] = [];
    const variantOnly = isVariantOnlyCategory(category);

    const storedMain = categoryMainLayoutByCategory?.[category];
    if (
        !excludeRecycledMainCandidate &&
        storedMain?.roofIds?.length &&
        (!variantOnly || storedMain.source === 'variant')
    ) {
        const catKey = getCategoryMainLayoutKey(category);
        candidates.push({
            ...storedMain,
            key: catKey,
            source: storedMain.source ?? 'layoutKey',
        });
    }

    const variants = (variantsByCategory[category] ?? []) as any[];
    for (const v of variants) {
        if (!v) continue;
        const modules = typeof v.modules === 'number' && Number.isFinite(v.modules) ? v.modules : 0;
        const production =
            typeof v.productionW === 'number' && Number.isFinite(v.productionW) ? v.productionW : 0;
        const roofIds = Array.isArray(v.roofIds) ? v.roofIds.filter(Boolean) : [];
        candidates.push({
            key: `variant:${category}:${v.id ?? v.label ?? modules}`,
            source: 'variant',
            variantId: typeof v.id === 'string' ? v.id : undefined,
            label: typeof v.label === 'string' ? v.label : undefined,
            roofIds,
            dir: v.dir === 'port' || v.dir === 'land' || v.dir === 'both' ? v.dir : null,
            minEff: typeof v.minEff === 'number' && Number.isFinite(v.minEff) ? v.minEff : null,
            production,
            modules,
            efficiency: typeof v.efficiency === 'number' && Number.isFinite(v.efficiency) ? v.efficiency : null,
            solarAccess: typeof v.solarAccess === 'number' && Number.isFinite(v.solarAccess) ? v.solarAccess : null,
            multiplier: typeof v.multiplier === 'number' && Number.isFinite(v.multiplier) ? v.multiplier : null,
            snapshotUrl: typeof v.snapshotUrl === 'string' ? v.snapshotUrl : undefined,
            snapshotError:
                typeof v.snapshotCaptureError === 'string' ? v.snapshotCaptureError : undefined,
            roofCount:
                typeof v.roofCount === 'number' && Number.isFinite(v.roofCount) && v.roofCount > 0
                    ? v.roofCount
                    : roofIds.length,
            modulesLayout: Array.isArray(v.modulesLayout) && v.modulesLayout.length ? v.modulesLayout : undefined,
        });
    }

    const catKey = getCategoryMainLayoutKey(category);
    if (!variantOnly && !excludeRecycledMainCandidate && !candidates.some((c) => c.key === catKey)) {
        const production =
            typeof layoutProductionByKey[catKey] === 'number' && Number.isFinite(layoutProductionByKey[catKey])
                ? layoutProductionByKey[catKey]
                : 0;
        const modules =
            typeof layoutModulesByKey[catKey] === 'number' && Number.isFinite(layoutModulesByKey[catKey])
                ? layoutModulesByKey[catKey]
                : 0;
        const metrics = layoutMetricsByKey[catKey];
        if (production > 0 && modules > 0 && metrics) {
            let roofIds = storedMain?.roofIds?.filter(Boolean) ?? [];
            if (!roofIds.length) {
                for (const v of variants) {
                    const ids = Array.isArray(v?.roofIds) ? v.roofIds.filter(Boolean) : [];
                    if (ids.length) {
                        roofIds = ids;
                        break;
                    }
                }
            }
            if (!roofIds.length) {
                roofIds = roofIdsByLayoutKey(catKey);
            }
            if (roofIds.length) {
                candidates.push({
                    key: catKey,
                    source: 'layoutKey',
                    variantId: storedMain?.variantId,
                    label: storedMain?.label,
                    roofIds,
                    dir: storedMain?.dir ?? null,
                    minEff: storedMain?.minEff ?? null,
                    production,
                    modules,
                    efficiency:
                        typeof metrics?.efficiency === 'number' && Number.isFinite(metrics.efficiency)
                            ? metrics.efficiency
                            : storedMain?.efficiency ?? null,
                    solarAccess:
                        typeof metrics?.solarAccess === 'number' && Number.isFinite(metrics.solarAccess)
                            ? metrics.solarAccess
                            : storedMain?.solarAccess ?? null,
                    multiplier:
                        typeof metrics?.multiplier === 'number' && Number.isFinite(metrics.multiplier)
                            ? metrics.multiplier
                            : storedMain?.multiplier ?? null,
                    snapshotUrl: layoutSnapshotsByKey[catKey] ?? storedMain?.snapshotUrl,
                    treeCount: metrics?.treeCount ?? storedMain?.treeCount ?? null,
                    roofCount: metrics?.roofCount ?? storedMain?.roofCount ?? null,
                    modulesLayout: storedMain?.modulesLayout?.length ? storedMain.modulesLayout : undefined,
                });
            }
        }
    }

    if (!variantOnly) {
        const categoryLayoutKey = resolveLayoutKeyForCategory(category, roofCombinationsFromAi);
        if (categoryLayoutKey) {
            pushLayoutKeyCandidate(candidates, categoryLayoutKey, args);
        }
        // Only attach the global default max-fill to categories that use all roofs.
        // Other categories (e.g. Balanced with 4 Gemini roofs) should rank their own
        // combo + variants, not inherit the 8-roof default simulation as a top candidate.
        if (categoryLayoutKey === 'default') {
            pushLayoutKeyCandidate(candidates, 'default', args);
        }
    }

    return candidates;
}

export function getLayoutCategoryNames(): string[] {
    return Object.keys(categoryLayoutVariantDefs).filter(
        (name) => name !== ONE_DRAW_DEFAULT_MAXFILL_CATEGORY,
    );
}
